import React from "react";
import { useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Button, ProgressIndicator, Pager, EmptyItem } from "rhombus-react";

import { CREATIVE_ROW_COUNT_OPTIONS } from "utils/constants";
import { useCollectionFiltering } from "utils/DataGrid";

import { QrDesigns } from "reducers/QrDesigns";
import { hasActiveFilters } from "reducers/common/ColumnFilters";

import { QR_CODES_DESIGNS_COLUMNS } from "widgets/grid/columns/QrCodesColumns";
import FilterTagList from "widgets/toolbars/FilterTagList";
import Permissioned from "widgets/Permissioned";

import Toolbar from "routes/qrcodes/Toolbar";
import Grid from "./Grid";

import "./QrCodesGrid.css";

export default function QrCodesDesignsGrid() {
	const { libraryId } = useParams();
	const dispatch = useDispatch();

	const qrCodesState = useSelector(state => state.qrDesigns);

	const {
		items: designs,
		isLoading,
		totalItems,
		totalPages,
		hasErrorLoading,
	} = qrCodesState;

	const handleSearch = newSearchTerm => dispatch(QrDesigns.setSearchTerm(newSearchTerm));

	const {
		filterInfo: { page, rpp, searchTerm, sort },
		columnFilters,
	} = useCollectionFiltering(
		qrCodesState,
		QrDesigns.setAdvancedFilter,
		() => {
			dispatch(QrDesigns.clearQrDesigns()).then(() => {
				dispatch(QrDesigns.getQrDesigns(libraryId));
			});
		}
	);

	function resetColumnFilters() {
		dispatch(QrDesigns.clearColumnFilters());
	}

	function retryLoading() {
		dispatch(QrDesigns.getQrDesigns(libraryId));
	}

	let hasActive = hasActiveFilters(qrCodesState)
		, hasFiltersAppliedClass = hasActive ? "has-advanced-filters-applied" : ""
		;

	let emptyProps = {
		className: "empty-designs no-qr-codes",
		title: "No QR code designs created yet",
		//The message is an empty string to avoid the default message from the EmptyItem component
		message: " ",
		src: "/images/notification-icon.svg",
		buttons: (
			<Permissioned
				domain="Creative View"
				action="Upload Assets"
			>
				<Link to={`/library/${libraryId}/qr-designs/new/`}>
					<Button>New QR Code Design</Button>
				</Link>
			</Permissioned>
		),
	};

	if (hasErrorLoading) {
		emptyProps = {
			className: "empty-designs no-qr-codes",
			title: "There was a problem loading QR Code Designs",
			//The message is an empty string to avoid the default message from the EmptyItem component
			message: " ",
			icon: "fas fa-bug",
			buttons: [
				<Button key="reload" icon="refresh"
					onClick={retryLoading}>
					Re-Try
				</Button>
			],
		};
	} else if (searchTerm) {
		emptyProps = {
			className: "empty-qr-codes",
			title: "No search results to display",
			message: "Try adjusting your search to find what you are looking for",
			src: "/images/noSearch.svg",
			buttons: [
				<Button key="clear" onClick={() => dispatch(QrDesigns.setSearchTerm(""))}>
					Clear Search
				</Button>,
			],
		};
	} else if (hasActive) {
		emptyProps = {
			className: "empty-qr-codes",
			title: "No results to display",
			message: "Try adjusting your filters to find what you are looking for",
			src: "/images/noFiles.svg",
			buttons: [
				<Button key="clear" onClick={resetColumnFilters}>Clear All Filters</Button>
			],
		};
	}

	const pagerProps={
		showItemCount: true,
		initialPage: page,
		forcePage: page,
		pageSize: rpp,
		pageCount: totalPages,
		maxItems: totalItems,
		showItemWord: "",
		rowCountItems: CREATIVE_ROW_COUNT_OPTIONS,
		disabled: isLoading,

		onPageChange: newPage => dispatch(QrDesigns.setPage(newPage)),
		onRowCountChange: newRPP => dispatch(QrDesigns.setRPP(newRPP)),
	};

	const $filterList = (
		<FilterTagList
			key="filter-tags"
			Reducer = {QrDesigns}
			filters = {columnFilters}
			resetFilters={resetColumnFilters}
		/>
	);

	return (
		<div className={`${hasFiltersAppliedClass} qr-code-designs-div`}>
			<ProgressIndicator isLoading={isLoading} />
			{(totalItems > 0 || searchTerm || hasActive) && (
				<Toolbar key = "toolbar"
					searchTerm ={searchTerm}
					handleSearch ={handleSearch}
					libraryId={libraryId}
				/>
			)}
			{hasActive && $filterList}
			{totalItems === 0 && !isLoading && (
				<EmptyItem key="empty" {...emptyProps} />
			)}
			{totalItems > 0 && (
				<>
					<div className="qr-code-designs-div qr-code-grid" data-testid="grid">
						<Grid qrCodes={designs} libraryId={libraryId} columns={QR_CODES_DESIGNS_COLUMNS} sort={sort} />
						<Pager {...pagerProps} />
					</div>
				</>
			)}
		</div>
	);
}

QrCodesDesignsGrid.displayName = "QrCodesDesignsGrid";
